// web-commands.ts — the PER-APP half of the web shape: maps mood_tracker's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each (one entry per calendar day, matching db.rs::create_entry). The
// one derived command (compute_signals) is the app's "paid draw" computed on-device in
// signals.rs — on web it runs the faithful JS port in web-signals.ts (same aggregates, same
// ceilings), so the AI reflection + Ask features work on the web surface too.

import type { CommandHandler, WebStore, Row } from "./web-runtime";
import { computeWebSignals, type SignalEntry } from "./web-signals";
import { browserSearch, browserFetch } from "./web-search-browser";

const TABLE = "mood_entries";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}
function clampScore(s: number): number {
  return Math.min(5, Math.max(1, Math.round(s)));
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  // The LATEST entry that day — mirrors db.rs's ORDER BY logged_at DESC. With several entries a
  // day, "the entry for this date" is ambiguous unless it means the most recent one.
  const findByDate = (date: string): Row | undefined =>
    db.all(TABLE)
      .filter((r) => (r as { logged_on?: string }).logged_on === date)
      .sort((a, b) => String((b as { logged_at?: string }).logged_at ?? "").localeCompare(
                      String((a as { logged_at?: string }).logged_at ?? "")))[0];

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    list_entries: ({ from, to, limit }) => {
      let rows = db.all(TABLE);
      if (from) rows = rows.filter((r) => (r as { logged_on?: string }).logged_on! >= String(from));
      if (to) rows = rows.filter((r) => (r as { logged_on?: string }).logged_on! <= String(to));
      rows.sort((a, b) => {
        const byDate = String((b as { logged_on?: string }).logged_on ?? "").localeCompare(
          String((a as { logged_on?: string }).logged_on ?? ""),
        );
        if (byDate !== 0) return byDate;
        const byTime = String((b as { logged_at?: string }).logged_at ?? "").localeCompare(
          String((a as { logged_at?: string }).logged_at ?? ""),
        );
        if (byTime !== 0) return byTime;
        return String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        );
      });
      const cap = typeof limit === "number" ? limit : 50000;
      return rows.slice(0, cap);
    },
    get_entry: ({ id }) => db.get(TABLE, String(id)),
    // The LATEST entry that day — several may exist now, and callers ask this as a yes/no
    // "have I logged today?".
    get_entry_for_date: ({ date }) => findByDate(String(date)) ?? null,
    create_entry: ({ input }) => {
      const i = input as Record<string, unknown>;
      const logged_on = (i.logged_on as string | undefined) ?? todayLocal();
      // The one-per-day rule is GONE (mirrors db.rs) — a mood is not a property of a day, and
      // "fine this morning, awful by six" could not be recorded at all under the old rule.
      const at = typeof i.logged_at === "string" && /^\d{2}:\d{2}$/.test(i.logged_at)
        ? i.logged_at
        : new Date().toTimeString().slice(0, 5);
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        logged_on,
        logged_at: at,
        score: clampScore(Number(i.score ?? 3)),
        note: typeof i.note === "string" ? i.note.trim() : "",
        tags: Array.isArray(i.tags) ? i.tags : [],
        created_at: now,
        updated_at: now,
      };
      return db.insert(TABLE, row);
    },
    update_entry: ({ id, patch }) => {
      db.update(TABLE, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_entry: ({ id }) => {
      db.remove(TABLE, String(id));
      return null;
    },
    // Derived weekly-reflection signals — native side computes these in signals.rs; on web the
    // faithful JS port (web-signals.ts) computes the SAME aggregates from the WebStore rows.
    compute_signals: ({ today }) =>
      computeWebSignals(
        db.all(TABLE).map((r) => ({
          logged_on: String((r as { logged_on?: unknown }).logged_on ?? ""),
          score: clampScore(Number((r as { score?: unknown }).score ?? 3)),
          tags: Array.isArray((r as { tags?: unknown }).tags) ? ((r as { tags?: unknown }).tags as string[]) : [],
        })) as SignalEntry[],
        String(today ?? todayLocal()),
      ),
  };
}
